import React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react'
import { debounce, last } from 'lodash'

import TabStore from 'stores/tab/TabStore'
import TelemetryStore from 'stores/telemetry/TelemetryStore'

import IconButton from 'components/iconButton/IconButton'
import Dropdown from 'components/dropdown/Dropdown'

import Plus from 'assets/icons/plus.svg'

import styles from './whereFilter.scss'
import FieldStore from 'stores/field/FieldStore'

interface IProps {
  whereId: string
}

class WhereFilterRow extends React.PureComponent<IProps> {
  runScriptDebounced = debounce(
    () => {
      const script = TabStore.activeTab?.session!.script!
      script.run()
    },
    500,
    {
      leading: false,
      trailing: true,
    }
  )

  handleChangeField = ({ id: fieldId }: { id: string }, index: number) => {
    const { whereId } = this.props

    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!
    const where = script.where.get(whereId)
    if (!where) {
      // Should never happen
      return
    }
    script.update({ frozen: false })

    // Create an artificial  limit on the number of levels of filters you can have (2)
    if (index === 0) {
      const field = FieldStore.get(fieldId)
      if (field?.isRelation) {
        const relationsScalarDefaultId = FieldStore.get(fieldId)?.typeAsModel
          ?.uniqueIdentifiers[0].id
        where.update({ fieldIds: [fieldId, relationsScalarDefaultId!] })
      } else {
        where.update({ fieldIds: [fieldId] })
      }
    } else {
      const fieldsCopy = [...where.fieldIds]
      fieldsCopy[index] = fieldId
      where.update({ fieldIds: fieldsCopy })
    }

    if (!where.supportedOperations.includes(where.operation)) {
      // If the already selected operation is now invalid (because of the field change), switch to the first valid operation
      this.handleChangeOperation({ id: where.supportedOperations[0] })
    }

    if (['in', 'notIn'].includes(where.operation)) {
      // For "array" operations, change the value to an array
      this.handleChangeValue('[]')
    }

    this.runScriptDebounced()

    TelemetryStore.send({
      command: 'filter_change',
      commandDetails: {
        total_filters_count: script.where.size,
        field_types: where.fields.map(f => f.type),
        operation: where.operation,
      },
    })
  }

  handleChangeOperation = ({ id: operation }: { id: string }) => {
    const { whereId } = this.props

    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!
    const where = script.where.get(whereId)
    if (!where) {
      return
    }

    script.update({ frozen: false })
    where.update({ operation })

    if (['in', 'notIn'].includes(where.operation)) {
      this.handleChangeValue('[]')
    }
    if (['isNull', 'isNotNull'].includes(where.operation)) {
      this.handleChangeValue('')
    }

    this.runScriptDebounced()

    TelemetryStore.send({
      command: 'filter_change',
      commandDetails: {
        total_filters_count: script.where.size,
        field_types: where.fields.map(f => f.type),
        operation: where.operation,
      },
    })
  }

  handleChangeValue = (value: string) => {
    const { whereId } = this.props

    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!
    script.update({ frozen: false })

    const where = script.where.get(whereId)
    if (!where) {
      return
    }

    where.update({ value })

    this.runScriptDebounced()
  }

  handleDelete = () => {
    const { whereId } = this.props

    TabStore.activeTab?.update({ preview: false })

    const script = TabStore.activeTab?.session!.script!
    script.where.remove(whereId)
    this.runScriptDebounced()
  }

  render() {
    const { whereId } = this.props
    const session = TabStore.activeTab!.session!
    const where = session.script.where.get(whereId)

    if (!where) {
      return null
    }

    return (
      <>
        {/* If you're wondering why each element is wrapped in a seemingly useless div:
        1. We want grid-like behaviour: every column must be the same width, to avoid looking messy
        2. We want to control the background color and border per row
        This isn't possible when the elements themselves are CSS Grid cells, so we're using CSS Grid to layout the divs. The divs then act as containers for the real inputs / dropdowns
        */}

        <div
          className={cn(styles.fields, styles.cell, {
            [styles.invalid]: !where.isValid,
          })}
        >
          {/* Create an artificial limit on the number of levels you can filter on (2) */}
          {where.fields.slice(0, 2).map((field, index) => (
            <div className={styles.field} key={index}>
              <Dropdown
                data-cy={`where-filter__row__field${
                  index > 0 ? `_relation_scalars` : ''
                }`}
                data-cy-invalid={!where.isValid}
                buttonClassName={styles.dropdown}
                type="text"
                items={where.getFilterableFieldsAtIndex(index).map(f => ({
                  id: f.id,
                  label: f.name,
                }))}
                selectedItem={{
                  id: field.id,
                  label: field.name,
                }}
                onSelect={item => this.handleChangeField(item, index)}
              />
            </div>
          ))}
        </div>

        <div
          className={cn(styles.operation, styles.cell, {
            [styles.invalid]: !where.isValid,
          })}
        >
          <Dropdown
            data-cy="where-filter__row__operation"
            buttonClassName={styles.dropdown}
            type="text"
            items={where.supportedOperations.map(o => ({
              id: o,
              label: o,
            }))}
            selectedItem={{ id: where.operation, label: where.operation }}
            onSelect={this.handleChangeOperation}
          />
        </div>

        <div
          className={cn(styles.value, styles.cell, {
            [styles.invalid]: !where.isValid,
          })}
        >
          <input
            data-cy="where-filter__row__value"
            type="text"
            disabled={
              where.operation === 'isNull' || where.operation === 'isNotNull'
            }
            placeholder={last(where.fields)?.placeholder}
            value={where.value === null ? '' : where.value}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              this.handleChangeValue(e.currentTarget.value)
            } // This is an inline function because I want to reuse `handleChangeValue` in multiple places
          />
        </div>

        <div
          className={cn(styles.deleteButton, styles.cell, {
            [styles.invalid]: !where.isValid,
          })}
        >
          <IconButton
            data-cy="where-filter__row__delete-btn"
            onClick={this.handleDelete}
          >
            <Plus />
          </IconButton>
        </div>
      </>
    )
  }
}

export default observer(WhereFilterRow)
